import { useState, useEffect, Fragment } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { financeiroService, ContratoAberto, calcularRecebimentosPrevistos, calcularTotalParcelasPrevistas, ContaPagar, CustoObra, RecebimentoPrevisto, ContaReceber } from '@/services/financeiroService';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Search, FileText, ChevronDown, ChevronRight, Plus, Trash2, Edit } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';

interface ContratosAbertoProps {
  /** Quando igual a 'contratos', a aba está visível; usado para recarregar dados ao exibir (ex.: após registrar recebimento em Contas a receber). */
  tabAtiva?: string;
}

export function ContratosAberto({ tabAtiva = 'contratos' }: ContratosAbertoProps) {
  const [contratos, setContratos] = useState<ContratoAberto[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [contratosExpandidos, setContratosExpandidos] = useState<Set<string>>(new Set());
  const [contasReceber, setContasReceber] = useState<ContaReceber[]>([]);
  const [despesasPorContrato, setDespesasPorContrato] = useState<Map<string, ContaPagar[]>>(new Map());
  const [impostosPorContrato, setImpostosPorContrato] = useState<Map<string, CustoObra[]>>(new Map());
  const [todasDespesas, setTodasDespesas] = useState<ContaPagar[]>([]);
  const [todosImpostos, setTodosImpostos] = useState<CustoObra[]>([]);
  const [impostoDialogOpen, setImpostoDialogOpen] = useState(false);
  const [impostoSelecionado, setImpostoSelecionado] = useState<CustoObra | null>(null);
  const [contratoIdParaImposto, setContratoIdParaImposto] = useState<string>('');
  const [formImposto, setFormImposto] = useState({
    descricao: '',
    valor: '',
    data: new Date().toISOString().split('T')[0],
    observacoes: '',
  });

  // Carregar ao montar e sempre que a aba Contratos for exibida (ex.: após registrar recebimento em Contas a receber)
  useEffect(() => {
    if (tabAtiva === 'contratos') {
      carregarContratos();
    }
  }, [tabAtiva]);

  const carregarContratos = async () => {
    try {
      setLoading(true);
      const data = await financeiroService.listarContratosAberto();
      setContratos(data);
      
      // Carregar contas a receber para verificar pagamentos confirmados
      const contas = await financeiroService.listarContasReceber();
      setContasReceber(contas);

      // Carregar todas as despesas de todos os contratos para calcular lucro total
      const todasDespesas = await financeiroService.listarContasPagar();
      setTodasDespesas(todasDespesas.filter(d => d.contrato_id));

      // Carregar todos os impostos (custos de obras com categoria "Impostos") de todos os contratos
      const todosCustos = await financeiroService.listarCustosObras();
      const impostos = todosCustos.filter(c => c.categoria === 'Impostos' && c.contrato_id);
      setTodosImpostos(impostos);
    } catch (error) {
      console.error('Erro ao carregar contratos em aberto:', error);
      toast.error('Erro ao carregar contratos em aberto');
    } finally {
      setLoading(false);
    }
  };

  const contratosFiltrados = contratos.filter(contrato => {
    const matchBusca = busca === '' || 
      contrato.cliente_nome.toLowerCase().includes(busca.toLowerCase());
    return matchBusca;
  });

  // Calcular totais
  const totalContratos = contratosFiltrados.reduce((acc, c) => acc + c.valor_total, 0);
  const totalIntegracao = contratosFiltrados.reduce((acc, c) => acc + (c.integracao_valor_total || 0), 0);
  const totalRecebido = contratosFiltrados.reduce((acc, c) => acc + c.valor_recebido, 0);
  
  // Calcular receitas totais (soma de todos os recebimentos previstos)
  const totalReceitas = contratosFiltrados.reduce((acc, contrato) => {
    const recebimentosPrevistos = calcularRecebimentosPrevistos(contrato);
    return acc + recebimentosPrevistos.reduce((sum, r) => sum + r.valor, 0);
  }, 0);

  // Calcular despesas totais (apenas despesas vinculadas aos contratos filtrados)
  const contratosFiltradosIds = new Set(contratosFiltrados.map(c => c.id));
  const totalDespesas = todasDespesas
    .filter(d => d.contrato_id && contratosFiltradosIds.has(d.contrato_id))
    .reduce((acc, d) => acc + (d.valor || 0), 0);

  // Calcular impostos totais (apenas impostos vinculados aos contratos filtrados)
  const totalImpostos = todosImpostos
    .filter(i => i.contrato_id && contratosFiltradosIds.has(i.contrato_id))
    .reduce((acc, i) => acc + i.valor, 0);

  // Calcular lucro total
  const lucroTotal = totalReceitas - totalDespesas - totalImpostos;
  
  // Calcular percentual do lucro em relação à receita
  const percentualLucro = totalReceitas > 0 ? (lucroTotal / totalReceitas) * 100 : 0;

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'assinado':
        return <Badge variant="default" className="bg-success">Assinado</Badge>;
      case 'em_andamento':
        return <Badge variant="outline" className="border-blue-500 text-blue-500">Em Andamento</Badge>;
      case 'contrato_gerado':
        return <Badge variant="secondary">Contrato Gerado</Badge>;
      case 'proposta_aceita':
        return <Badge variant="outline">Proposta Aceita</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const formatarModalidade = (modalidade?: number): string => {
    const modalidades: { [key: number]: string } = {
      1: 'Pagamento integral na assinatura',
      2: 'Assinatura + Conclusão',
      3: 'Assinatura + Início + Conclusão',
      4: 'Assinatura + Início + Conclusão + Ligação',
      5: 'Pagamento integral na conclusão da obra'
    };
    return modalidades[modalidade || 1] || 'Não informado';
  };

  const toggleContratoExpandido = async (contratoId: string) => {
    const isExpandido = contratosExpandidos.has(contratoId);
    
    setContratosExpandidos(prev => {
      const novo = new Set(prev);
      if (novo.has(contratoId)) {
        novo.delete(contratoId);
      } else {
        novo.add(contratoId);
      }
      return novo;
    });

    // Se está expandindo e ainda não carregou as despesas e impostos, buscar
    if (!isExpandido) {
      try {
        // Buscar despesas
        if (!despesasPorContrato.has(contratoId)) {
          const despesas = await financeiroService.listarContasPagar({ contrato_id: contratoId });
          setDespesasPorContrato(prev => {
            const novo = new Map(prev);
            novo.set(contratoId, despesas);
            return novo;
          });
        }
        
        // Buscar impostos (custos de obras com categoria "Impostos")
        if (!impostosPorContrato.has(contratoId)) {
          const custos = await financeiroService.listarCustosObras({ contrato_id: contratoId });
          const impostos = custos.filter(c => c.categoria === 'Impostos');
          setImpostosPorContrato(prev => {
            const novo = new Map(prev);
            novo.set(contratoId, impostos);
            return novo;
          });
        }
      } catch (error) {
        console.error('Erro ao carregar dados do contrato:', error);
        toast.error('Erro ao carregar dados do contrato');
      }
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const calcularPercentual = (valor: number, receita: number): number => {
    if (receita === 0) return 0;
    return (valor / receita) * 100;
  };

  const formatPercentual = (percentual: number): string => {
    return `${percentual.toFixed(2)}%`;
  };

  /**
   * Verifica se a etapa foi recebida: critério único — existe transação em Contas a Receber
   * com mesmo contrato_id, mesma etapa (contrato_etapa) e status 'recebido'.
   * Retrocompatibilidade: se a conta não tiver contrato_etapa, usa match por descrição normalizada.
   */
  const verificarPagamentoConfirmado = (contratoId: string, recebimento: RecebimentoPrevisto): boolean => {
    const etapa = (recebimento.etapa || '').toLowerCase();
    const contasRecebidas = contasReceber.filter(
      c => c.contrato_id === contratoId && c.status === 'recebido'
    );
    for (const conta of contasRecebidas) {
      if (conta.contrato_etapa) {
        if (conta.contrato_etapa.toLowerCase() === etapa) return true;
      } else {
        const descricaoBase = (recebimento.descricao || '').split(' - Parcela')[0].trim();
        const contaDesc = (conta.descricao || '').split(' - Parcela')[0].trim();
        const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        if (norm(contaDesc).includes(norm(descricaoBase)) || norm(descricaoBase).includes(norm(contaDesc))) return true;
      }
    }
    return false;
  };

  /**
   * Retorna o valor efetivamente pago para a etapa (soma das transações recebidas com essa etapa).
   * Usado para exibir o valor real pago, mesmo que diferente do previsto em contrato.
   */
  const obterValorRecebidoEtapa = (contratoId: string, recebimento: RecebimentoPrevisto): number => {
    const etapa = (recebimento.etapa || '').toLowerCase();
    const contasRecebidas = contasReceber.filter(
      c => c.contrato_id === contratoId && c.status === 'recebido'
    );
    let total = 0;
    for (const conta of contasRecebidas) {
      if (conta.contrato_etapa) {
        if (conta.contrato_etapa.toLowerCase() === etapa) total += Number(conta.valor);
      } else {
        const descricaoBase = (recebimento.descricao || '').split(' - Parcela')[0].trim();
        const contaDesc = (conta.descricao || '').split(' - Parcela')[0].trim();
        const norm = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        if (norm(contaDesc).includes(norm(descricaoBase)) || norm(descricaoBase).includes(norm(contaDesc))) total += Number(conta.valor);
      }
    }
    return total;
  };

  const abrirDialogImposto = (contratoId: string, imposto?: CustoObra) => {
    setContratoIdParaImposto(contratoId);
    if (imposto) {
      setImpostoSelecionado(imposto);
      setFormImposto({
        descricao: imposto.descricao,
        valor: imposto.valor.toString(),
        data: imposto.data,
        observacoes: imposto.observacoes || '',
      });
    } else {
      setImpostoSelecionado(null);
      setFormImposto({
        descricao: '',
        valor: '',
        data: new Date().toISOString().split('T')[0],
        observacoes: '',
      });
    }
    setImpostoDialogOpen(true);
  };

  const salvarImposto = async () => {
    if (!formImposto.descricao.trim() || !formImposto.valor.trim()) {
      toast.error('Preencha descrição e valor do imposto');
      return;
    }

    const valor = parseFloat(formImposto.valor.replace(/[^\d,]/g, '').replace(',', '.'));
    if (isNaN(valor) || valor <= 0) {
      toast.error('Valor inválido');
      return;
    }

    try {
      if (impostoSelecionado) {
        // Atualizar imposto existente
        await financeiroService.atualizarCustoObra(impostoSelecionado.id, {
          descricao: formImposto.descricao,
          valor: valor,
          data: formImposto.data,
          observacoes: formImposto.observacoes || undefined,
        });
        toast.success('Imposto atualizado com sucesso');
      } else {
        // Criar novo imposto (inclui contrato_tipo para suportar contratos carregador)
        const contrato = contratos.find(c => c.id === contratoIdParaImposto);
        await financeiroService.criarCustoObra({
          contrato_id: contratoIdParaImposto,
          contrato_tipo: contrato?.tipo_contrato ?? 'fotovoltaico',
          descricao: formImposto.descricao,
          valor: valor,
          categoria: 'Impostos',
          data: formImposto.data,
          observacoes: formImposto.observacoes || undefined,
        });
        toast.success('Imposto adicionado com sucesso');
      }

      // Recarregar impostos do contrato
      const custos = await financeiroService.listarCustosObras({ contrato_id: contratoIdParaImposto });
      const impostos = custos.filter(c => c.categoria === 'Impostos');
      setImpostosPorContrato(prev => {
        const novo = new Map(prev);
        novo.set(contratoIdParaImposto, impostos);
        return novo;
      });

      setImpostoDialogOpen(false);
    } catch (error) {
      console.error('Erro ao salvar imposto:', error);
      toast.error('Erro ao salvar imposto');
    }
  };

  const deletarImposto = async (impostoId: string, contratoId: string) => {
    if (!confirm('Tem certeza que deseja excluir este imposto?')) {
      return;
    }

    try {
      await financeiroService.deletarCustoObra(impostoId);
      toast.success('Imposto excluído com sucesso');

      // Recarregar impostos do contrato
      const custos = await financeiroService.listarCustosObras({ contrato_id: contratoId });
      const impostos = custos.filter(c => c.categoria === 'Impostos');
      setImpostosPorContrato(prev => {
        const novo = new Map(prev);
        novo.set(contratoId, impostos);
        return novo;
      });
    } catch (error) {
      console.error('Erro ao deletar imposto:', error);
      toast.error('Erro ao deletar imposto');
    }
  };

  return (
    <div className="space-y-4 min-[990px]:space-y-6">
      <div>
        <h2 className="text-xl min-[990px]:text-2xl font-bold">Contratos em Aberto</h2>
        <p className="text-xs min-[990px]:text-sm text-muted-foreground">Acompanhe os valores pendentes de recebimento dos contratos</p>
      </div>

      <div className="grid grid-cols-1 min-[990px]:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2 min-[990px]:pb-3">
            <CardTitle className="text-xs min-[990px]:text-sm font-medium">Total Contratos</CardTitle>
          </CardHeader>
          <CardContent className="p-3 min-[990px]:p-6">
            <div className="text-xl min-[990px]:text-2xl font-bold">
              {formatCurrency(totalContratos)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2 min-[990px]:pb-3">
            <CardTitle className="text-xs min-[990px]:text-sm font-medium">Total Integração</CardTitle>
          </CardHeader>
          <CardContent className="p-3 min-[990px]:p-6">
            <div className="text-xl min-[990px]:text-2xl font-bold">
              {formatCurrency(totalIntegracao)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2 min-[990px]:pb-3">
            <CardTitle className="text-xs min-[990px]:text-sm font-medium">Total Recebido</CardTitle>
          </CardHeader>
          <CardContent className="p-3 min-[990px]:p-6">
            <div className="text-xl min-[990px]:text-2xl font-bold text-success">
              {formatCurrency(totalRecebido)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2 min-[990px]:pb-3">
            <CardTitle className="text-xs min-[990px]:text-sm font-medium">Lucro Total</CardTitle>
          </CardHeader>
          <CardContent className="p-3 min-[990px]:p-6">
            <div className={`text-xl min-[990px]:text-2xl font-bold ${lucroTotal >= 0 ? 'text-success' : 'text-destructive'}`}>
              {formatCurrency(lucroTotal)}
            </div>
            {totalReceitas > 0 && (
              <div className="text-xs text-muted-foreground mt-1">
                ({formatPercentual(percentualLucro)})
              </div>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2 min-[990px]:pb-3">
            <CardTitle className="text-xs min-[990px]:text-sm font-medium">Total de Contratos</CardTitle>
          </CardHeader>
          <CardContent className="p-3 min-[990px]:p-6">
            <div className="text-xl min-[990px]:text-2xl font-bold">
              {contratosFiltrados.length}
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="p-3 min-[990px]:p-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 min-[990px]:h-4 min-[990px]:w-4 text-muted-foreground" />
                <Input
                  placeholder="Buscar por cliente..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="pl-10 w-full h-8 min-[990px]:h-10 text-xs min-[990px]:text-sm"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-3 min-[990px]:p-6">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : contratosFiltrados.length === 0 ? (
            <div className="text-center p-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum contrato em aberto encontrado</p>
            </div>
          ) : (
            <>
              {/* Desktop: Tabela (a partir de 990px, alinhado às abas) */}
              <div className="hidden min-[990px]:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead>Cliente</TableHead>
                      <TableHead>Data</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Modalidade</TableHead>
                      <TableHead className="text-right">Valor Contrato</TableHead>
                      <TableHead className="text-right">Valor Integração</TableHead>
                      <TableHead className="text-right">Valor Recebido</TableHead>
                      <TableHead className="text-right">Valor Pendente</TableHead>
                      <TableHead>Parcelas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contratosFiltrados.map((contrato) => {
                      const recebimentosPrevistos = calcularRecebimentosPrevistos(contrato);
                      const isExpandido = contratosExpandidos.has(contrato.id);
                      
                      return (
                        <Fragment key={contrato.id}>
                          <TableRow>
                            <TableCell>
                              {recebimentosPrevistos.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleContratoExpandido(contrato.id)}
                                  className="h-8 w-8 p-0"
                                >
                                  {isExpandido ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge variant={contrato.tipo_contrato === 'carregador' ? 'secondary' : 'outline'} className="text-xs">
                                {contrato.tipo_contrato === 'carregador' ? 'Carregador' : 'Fotovoltaico'}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">{contrato.cliente_nome}</TableCell>
                            <TableCell>
                              {contrato.created_at 
                                ? format(new Date(contrato.created_at), "dd/MM/yyyy", { locale: ptBR })
                                : '-'
                              }
                            </TableCell>
                            <TableCell>{getStatusBadge(contrato.status)}</TableCell>
                            <TableCell className="text-xs">
                              {formatarModalidade(contrato.integracao_modalidade_pagamento)}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(contrato.valor_total)}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(contrato.integracao_valor_total || 0)}
                            </TableCell>
                            <TableCell className="text-right text-success">
                              {formatCurrency(contrato.valor_recebido)}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-warning">
                              {formatCurrency(contrato.valor_pendente)}
                            </TableCell>
                            <TableCell>
                              {contrato.parcelas_pagas} / {calcularTotalParcelasPrevistas(contrato)}
                            </TableCell>
                          </TableRow>
                          {isExpandido && (
                            <TableRow>
                              <TableCell colSpan={11} className="bg-muted/50 p-0">
                            <div className="p-4 space-y-4">
                              {/* Receitas */}
                              {recebimentosPrevistos.length > 0 && (
                                <>
                                  <h4 className="font-semibold mb-3 text-sm">Receitas</h4>
                                  <div className="space-y-2">
                                    {recebimentosPrevistos.map((recebimento, index) => (
                                      <div
                                        key={`${recebimento.etapa}-${index}`}
                                        className="flex items-center justify-between p-3 bg-background rounded-lg border"
                                      >
                                        <div className="flex-1">
                                          <div className="font-medium text-sm">{recebimento.descricao}</div>
                                          <div className="text-xs text-muted-foreground mt-1">
                                            {recebimento.percentual > 0 && (
                                              <span>{recebimento.percentual}% • </span>
                                            )}
                                            {recebimento.forma_pagamento}
                                            {recebimento.numero_parcelas > 1 && (
                                              <span> • {recebimento.numero_parcelas}x de {formatCurrency(recebimento.valor_parcela)}</span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-4">
                                          <div className="text-right">
                                            <div className="text-xs text-muted-foreground">Previsto</div>
                                            <div className="font-semibold text-sm">
                                              {formatCurrency(recebimento.valor)}
                                            </div>
                                            {verificarPagamentoConfirmado(contrato.id, recebimento) && (() => {
                                              const valorPago = obterValorRecebidoEtapa(contrato.id, recebimento);
                                              return valorPago > 0 ? (
                                                <div className="text-xs text-success mt-0.5">
                                                  Pago: {formatCurrency(valorPago)}
                                                </div>
                                              ) : null;
                                            })()}
                                            {recebimento.numero_parcelas > 1 && (
                                              <div className="text-xs text-muted-foreground">
                                                Total: {formatCurrency(recebimento.valor)}
                                              </div>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2">
                                            <span className="text-sm text-muted-foreground">Recebido?</span>
                                            <span className={`text-sm font-semibold ${
                                              verificarPagamentoConfirmado(contrato.id, recebimento)
                                                ? 'text-success'
                                                : 'text-muted-foreground'
                                            }`}>
                                              {verificarPagamentoConfirmado(contrato.id, recebimento) ? 'Sim' : 'Não'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                    <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/20 mt-2">
                                      <div className="font-semibold text-sm">Total de Receitas Previstas</div>
                                      <div className="font-bold text-sm text-primary">
                                        {formatCurrency(
                                          recebimentosPrevistos.reduce((acc, r) => acc + r.valor, 0)
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </>
                              )}

                              {/* Despesas */}
                              {(() => {
                                const despesas = despesasPorContrato.get(contrato.id) || [];
                                const totalDespesas = despesas.reduce((acc, d) => acc + (d.valor || 0), 0);
                                const totalReceitas = recebimentosPrevistos.reduce((acc, r) => acc + r.valor, 0);
                                const percentualDespesas = calcularPercentual(totalDespesas, totalReceitas);
                                
                                return (
                                  <>
                                    <h4 className="font-semibold mb-3 text-sm mt-4">Despesas</h4>
                                    {despesas.length === 0 ? (
                                      <div className="p-3 bg-background rounded-lg border text-sm text-muted-foreground">
                                        Nenhuma despesa vinculada a este contrato
                                      </div>
                                    ) : (
                                      <div className="space-y-2">
                                        {despesas.map((despesa) => (
                                          <div
                                            key={despesa.id}
                                            className="flex items-center justify-between p-3 bg-background rounded-lg border"
                                          >
                                            <div className="flex-1">
                                              <div className="font-medium text-sm">{despesa.descricao}</div>
                                              <div className="text-xs text-muted-foreground mt-1">
                                                {despesa.categoria}
                                                {despesa.fornecedor && <span> • {despesa.fornecedor}</span>}
                                                {despesa.data_pagamento && (
                                                  <span> • Pago em {format(new Date(despesa.data_pagamento), "dd/MM/yyyy", { locale: ptBR })}</span>
                                                )}
                                              </div>
                                            </div>
                                            <div className="text-right">
                                              <div className="font-semibold text-sm text-destructive">
                                                {formatCurrency(despesa.valor || 0)}
                                              </div>
                                              <div className="text-xs text-muted-foreground">
                                                {despesa.status === 'pago' ? 'Pago' : despesa.status === 'pendente' ? 'Pendente' : 'Vencido'}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                        <div className="flex items-center justify-between p-3 bg-destructive/5 rounded-lg border border-destructive/20 mt-2">
                                          <div className="font-semibold text-sm">Total de Despesas</div>
                                          <div className="text-right">
                                            <div className="font-bold text-sm text-destructive">
                                              {formatCurrency(totalDespesas)}
                                            </div>
                                            {totalReceitas > 0 && (
                                              <div className="text-xs text-muted-foreground mt-1">
                                                {formatPercentual(percentualDespesas)} da receita
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}

                              {/* Impostos */}
                              {(() => {
                                const impostos = impostosPorContrato.get(contrato.id) || [];
                                const totalImpostos = impostos.reduce((acc, i) => acc + i.valor, 0);
                                const totalReceitas = recebimentosPrevistos.reduce((acc, r) => acc + r.valor, 0);
                                const percentualImpostos = calcularPercentual(totalImpostos, totalReceitas);
                                
                                return (
                                  <>
                                    <div className="flex items-center justify-between mb-3 mt-4">
                                      <h4 className="font-semibold text-sm">Impostos</h4>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => abrirDialogImposto(contrato.id)}
                                        className="h-8"
                                      >
                                        <Plus className="h-3 w-3 mr-1" />
                                        Adicionar Imposto
                                      </Button>
                                    </div>
                                    {impostos.length === 0 ? (
                                      <div className="p-3 bg-background rounded-lg border text-sm text-muted-foreground">
                                        Nenhum imposto registrado para este contrato
                                      </div>
                                    ) : (
                                      <div className="space-y-2">
                                        {impostos.map((imposto) => (
                                          <div
                                            key={imposto.id}
                                            className="flex items-center justify-between p-3 bg-background rounded-lg border"
                                          >
                                            <div className="flex-1">
                                              <div className="font-medium text-sm">{imposto.descricao}</div>
                                              <div className="text-xs text-muted-foreground mt-1">
                                                {format(new Date(imposto.data), "dd/MM/yyyy", { locale: ptBR })}
                                                {imposto.observacoes && <span> • {imposto.observacoes}</span>}
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <div className="text-right">
                                                <div className="font-semibold text-sm text-orange-600">
                                                  {formatCurrency(imposto.valor)}
                                                </div>
                                              </div>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => abrirDialogImposto(contrato.id, imposto)}
                                                className="h-8 w-8 p-0"
                                              >
                                                <Edit className="h-3 w-3" />
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => deletarImposto(imposto.id, contrato.id)}
                                                className="h-8 w-8 p-0 text-destructive"
                                              >
                                                <Trash2 className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          </div>
                                        ))}
                                        <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800 mt-2">
                                          <div className="font-semibold text-sm">Total de Impostos</div>
                                          <div className="text-right">
                                            <div className="font-bold text-sm text-orange-600">
                                              {formatCurrency(totalImpostos)}
                                            </div>
                                            {totalReceitas > 0 && (
                                              <div className="text-xs text-muted-foreground mt-1">
                                                {formatPercentual(percentualImpostos)} da receita
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}

                              {/* Resultado (Lucro/Prejuízo) */}
                              {(() => {
                                const totalReceitas = recebimentosPrevistos.reduce((acc, r) => acc + r.valor, 0);
                                const despesas = despesasPorContrato.get(contrato.id) || [];
                                const totalDespesas = despesas.reduce((acc, d) => acc + (d.valor || 0), 0);
                                const impostos = impostosPorContrato.get(contrato.id) || [];
                                const totalImpostos = impostos.reduce((acc, i) => acc + i.valor, 0);
                                const resultado = totalReceitas - totalDespesas - totalImpostos;
                                const isLucro = resultado >= 0;
                                const percentualLucro = calcularPercentual(Math.abs(resultado), totalReceitas);
                                
                                return (
                                  <div className="mt-4">
                                    <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                                      isLucro 
                                        ? 'bg-success/10 border-success/30' 
                                        : 'bg-destructive/10 border-destructive/30'
                                    }`}>
                                      <div className="font-semibold text-base">
                                        {isLucro ? 'Lucro' : 'Prejuízo'}
                                      </div>
                                      <div className="text-right">
                                        <div className={`font-bold text-lg ${
                                          isLucro ? 'text-success' : 'text-destructive'
                                        }`}>
                                          {formatCurrency(Math.abs(resultado))}
                                        </div>
                                        {totalReceitas > 0 && (
                                          <div className={`text-sm mt-1 ${
                                            isLucro ? 'text-success' : 'text-destructive'
                                          }`}>
                                            {formatPercentual(percentualLucro)} da receita
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-2 text-center">
                                      Receitas: {formatCurrency(totalReceitas)} - Despesas: {formatCurrency(totalDespesas)} - Impostos: {formatCurrency(totalImpostos)} = {isLucro ? 'Lucro' : 'Prejuízo'}: {formatCurrency(Math.abs(resultado))}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
              </div>

              {/* Abaixo de 990px: Cards */}
              <div className="min-[990px]:hidden space-y-4">
                {contratosFiltrados.map((contrato) => {
                  const recebimentosPrevistos = calcularRecebimentosPrevistos(contrato);
                  const isExpandido = contratosExpandidos.has(contrato.id);
                  
                  return (
                    <Card key={contrato.id}>
                      <CardContent className="p-4">
                        <div className="space-y-3">
                          {/* Header do Card */}
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <Badge variant={contrato.tipo_contrato === 'carregador' ? 'secondary' : 'outline'} className="text-xs">
                                  {contrato.tipo_contrato === 'carregador' ? 'Carregador' : 'Fotovoltaico'}
                                </Badge>
                                <h3 className="font-semibold text-base truncate">{contrato.cliente_nome}</h3>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">
                                {contrato.created_at 
                                  ? format(new Date(contrato.created_at), "dd/MM/yyyy", { locale: ptBR })
                                  : '-'
                                }
                              </p>
                            </div>
                            <div className="flex flex-col items-end gap-2">
                              {getStatusBadge(contrato.status)}
                              {recebimentosPrevistos.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleContratoExpandido(contrato.id)}
                                  className="h-8 w-8 p-0"
                                >
                                  {isExpandido ? (
                                    <ChevronDown className="h-4 w-4" />
                                  ) : (
                                    <ChevronRight className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </div>

                          {/* Valores */}
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>
                              <p className="text-xs text-muted-foreground">Valor Contrato</p>
                              <p className="font-semibold">{formatCurrency(contrato.valor_total)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Valor Integração</p>
                              <p className="font-semibold">{formatCurrency(contrato.integracao_valor_total || 0)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Valor Recebido</p>
                              <p className="font-semibold text-success">{formatCurrency(contrato.valor_recebido)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-muted-foreground">Valor Pendente</p>
                              <p className="font-semibold text-warning">{formatCurrency(contrato.valor_pendente)}</p>
                            </div>
                          </div>

                          {/* Modalidade e Parcelas */}
                          <div className="flex items-center justify-between text-xs border-t pt-2">
                            <div className="flex-1">
                              <p className="text-muted-foreground">Modalidade</p>
                              <p className="mt-1">{formatarModalidade(contrato.integracao_modalidade_pagamento)}</p>
                            </div>
                            <div className="text-right ml-4">
                              <p className="text-muted-foreground">Parcelas</p>
                              <p className="mt-1 font-semibold">
                                {contrato.parcelas_pagas} / {calcularTotalParcelasPrevistas(contrato)}
                              </p>
                            </div>
                          </div>

                          {/* Conteúdo Expandido */}
                          {isExpandido && (
                            <div className="mt-4 pt-4 border-t space-y-4">
                              {/* Receitas */}
                              {recebimentosPrevistos.length > 0 && (
                                <>
                                  <h4 className="font-semibold mb-3 text-sm">Receitas</h4>
                                  <div className="space-y-2">
                                    {recebimentosPrevistos.map((recebimento, index) => (
                                      <div
                                        key={`${recebimento.etapa}-${index}`}
                                        className="flex items-center justify-between p-3 bg-background rounded-lg border"
                                      >
                                        <div className="flex-1 min-w-0">
                                          <div className="font-medium text-sm">{recebimento.descricao}</div>
                                          <div className="text-xs text-muted-foreground mt-1">
                                            {recebimento.percentual > 0 && (
                                              <span>{recebimento.percentual}% • </span>
                                            )}
                                            {recebimento.forma_pagamento}
                                            {recebimento.numero_parcelas > 1 && (
                                              <span> • {recebimento.numero_parcelas}x de {formatCurrency(recebimento.valor_parcela)}</span>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-3 ml-2">
                                          <div className="text-right">
                                            <div className="text-xs text-muted-foreground">Previsto</div>
                                            <div className="font-semibold text-sm">
                                              {formatCurrency(recebimento.valor)}
                                            </div>
                                            {verificarPagamentoConfirmado(contrato.id, recebimento) && (() => {
                                              const valorPago = obterValorRecebidoEtapa(contrato.id, recebimento);
                                              return valorPago > 0 ? (
                                                <div className="text-xs text-success mt-0.5">
                                                  Pago: {formatCurrency(valorPago)}
                                                </div>
                                              ) : null;
                                            })()}
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <span className="text-xs text-muted-foreground">Recebido?</span>
                                            <span className={`text-xs font-semibold ${
                                              verificarPagamentoConfirmado(contrato.id, recebimento)
                                                ? 'text-success'
                                                : 'text-muted-foreground'
                                            }`}>
                                              {verificarPagamentoConfirmado(contrato.id, recebimento) ? 'Sim' : 'Não'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                    <div className="flex items-center justify-between p-3 bg-primary/5 rounded-lg border border-primary/20 mt-2">
                                      <div className="font-semibold text-sm">Total de Receitas Previstas</div>
                                      <div className="font-bold text-sm text-primary">
                                        {formatCurrency(
                                          recebimentosPrevistos.reduce((acc, r) => acc + r.valor, 0)
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </>
                              )}

                              {/* Despesas */}
                              {(() => {
                                const despesas = despesasPorContrato.get(contrato.id) || [];
                                const totalDespesas = despesas.reduce((acc, d) => acc + (d.valor || 0), 0);
                                const totalReceitas = recebimentosPrevistos.reduce((acc, r) => acc + r.valor, 0);
                                const percentualDespesas = calcularPercentual(totalDespesas, totalReceitas);
                                
                                return (
                                  <>
                                    <h4 className="font-semibold mb-3 text-sm mt-4">Despesas</h4>
                                    {despesas.length === 0 ? (
                                      <div className="p-3 bg-background rounded-lg border text-sm text-muted-foreground">
                                        Nenhuma despesa vinculada a este contrato
                                      </div>
                                    ) : (
                                      <div className="space-y-2">
                                        {despesas.map((despesa) => (
                                          <div
                                            key={despesa.id}
                                            className="flex items-center justify-between p-3 bg-background rounded-lg border"
                                          >
                                            <div className="flex-1 min-w-0">
                                              <div className="font-medium text-sm">{despesa.descricao}</div>
                                              <div className="text-xs text-muted-foreground mt-1">
                                                {despesa.categoria}
                                                {despesa.fornecedor && <span> • {despesa.fornecedor}</span>}
                                                {despesa.data_pagamento && (
                                                  <span> • Pago em {format(new Date(despesa.data_pagamento), "dd/MM/yyyy", { locale: ptBR })}</span>
                                                )}
                                              </div>
                                            </div>
                                            <div className="text-right ml-2">
                                              <div className="font-semibold text-sm text-destructive">
                                                {formatCurrency(despesa.valor || 0)}
                                              </div>
                                              <div className="text-xs text-muted-foreground">
                                                {despesa.status === 'pago' ? 'Pago' : despesa.status === 'pendente' ? 'Pendente' : 'Vencido'}
                                              </div>
                                            </div>
                                          </div>
                                        ))}
                                        <div className="flex items-center justify-between p-3 bg-destructive/5 rounded-lg border border-destructive/20 mt-2">
                                          <div className="font-semibold text-sm">Total de Despesas</div>
                                          <div className="text-right">
                                            <div className="font-bold text-sm text-destructive">
                                              {formatCurrency(totalDespesas)}
                                            </div>
                                            {totalReceitas > 0 && (
                                              <div className="text-xs text-muted-foreground mt-1">
                                                {formatPercentual(percentualDespesas)} da receita
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}

                              {/* Impostos */}
                              {(() => {
                                const impostos = impostosPorContrato.get(contrato.id) || [];
                                const totalImpostos = impostos.reduce((acc, i) => acc + i.valor, 0);
                                const totalReceitas = recebimentosPrevistos.reduce((acc, r) => acc + r.valor, 0);
                                const percentualImpostos = calcularPercentual(totalImpostos, totalReceitas);
                                
                                return (
                                  <>
                                    <div className="flex items-center justify-between mb-3 mt-4">
                                      <h4 className="font-semibold text-sm">Impostos</h4>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => abrirDialogImposto(contrato.id)}
                                        className="h-8 text-xs"
                                      >
                                        <Plus className="h-3 w-3 mr-1" />
                                        Adicionar
                                      </Button>
                                    </div>
                                    {impostos.length === 0 ? (
                                      <div className="p-3 bg-background rounded-lg border text-sm text-muted-foreground">
                                        Nenhum imposto registrado para este contrato
                                      </div>
                                    ) : (
                                      <div className="space-y-2">
                                        {impostos.map((imposto) => (
                                          <div
                                            key={imposto.id}
                                            className="flex items-center justify-between p-3 bg-background rounded-lg border"
                                          >
                                            <div className="flex-1 min-w-0">
                                              <div className="font-medium text-sm">{imposto.descricao}</div>
                                              <div className="text-xs text-muted-foreground mt-1">
                                                {format(new Date(imposto.data), "dd/MM/yyyy", { locale: ptBR })}
                                                {imposto.observacoes && <span> • {imposto.observacoes}</span>}
                                              </div>
                                            </div>
                                            <div className="flex items-center gap-2 ml-2">
                                              <div className="text-right">
                                                <div className="font-semibold text-sm text-orange-600">
                                                  {formatCurrency(imposto.valor)}
                                                </div>
                                              </div>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => abrirDialogImposto(contrato.id, imposto)}
                                                className="h-8 w-8 p-0"
                                              >
                                                <Edit className="h-3 w-3" />
                                              </Button>
                                              <Button
                                                size="sm"
                                                variant="ghost"
                                                onClick={() => deletarImposto(imposto.id, contrato.id)}
                                                className="h-8 w-8 p-0 text-destructive"
                                              >
                                                <Trash2 className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          </div>
                                        ))}
                                        <div className="flex items-center justify-between p-3 bg-orange-50 dark:bg-orange-950/20 rounded-lg border border-orange-200 dark:border-orange-800 mt-2">
                                          <div className="font-semibold text-sm">Total de Impostos</div>
                                          <div className="text-right">
                                            <div className="font-bold text-sm text-orange-600">
                                              {formatCurrency(totalImpostos)}
                                            </div>
                                            {totalReceitas > 0 && (
                                              <div className="text-xs text-muted-foreground mt-1">
                                                {formatPercentual(percentualImpostos)} da receita
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}

                              {/* Resultado (Lucro/Prejuízo) */}
                              {(() => {
                                const totalReceitas = recebimentosPrevistos.reduce((acc, r) => acc + r.valor, 0);
                                const despesas = despesasPorContrato.get(contrato.id) || [];
                                const totalDespesas = despesas.reduce((acc, d) => acc + (d.valor || 0), 0);
                                const impostos = impostosPorContrato.get(contrato.id) || [];
                                const totalImpostos = impostos.reduce((acc, i) => acc + i.valor, 0);
                                const resultado = totalReceitas - totalDespesas - totalImpostos;
                                const isLucro = resultado >= 0;
                                const percentualLucro = calcularPercentual(Math.abs(resultado), totalReceitas);
                                
                                return (
                                  <div className="mt-4">
                                    <div className={`flex items-center justify-between p-4 rounded-lg border-2 ${
                                      isLucro 
                                        ? 'bg-success/10 border-success/30' 
                                        : 'bg-destructive/10 border-destructive/30'
                                    }`}>
                                      <div className="font-semibold text-base">
                                        {isLucro ? 'Lucro' : 'Prejuízo'}
                                      </div>
                                      <div className="text-right">
                                        <div className={`font-bold text-lg ${
                                          isLucro ? 'text-success' : 'text-destructive'
                                        }`}>
                                          {formatCurrency(Math.abs(resultado))}
                                        </div>
                                        {totalReceitas > 0 && (
                                          <div className={`text-sm mt-1 ${
                                            isLucro ? 'text-success' : 'text-destructive'
                                          }`}>
                                            {formatPercentual(percentualLucro)} da receita
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                    <div className="text-xs text-muted-foreground mt-2 text-center">
                                      Receitas: {formatCurrency(totalReceitas)} - Despesas: {formatCurrency(totalDespesas)} - Impostos: {formatCurrency(totalImpostos)} = {isLucro ? 'Lucro' : 'Prejuízo'}: {formatCurrency(Math.abs(resultado))}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog de Imposto */}
      <Dialog open={impostoDialogOpen} onOpenChange={setImpostoDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {impostoSelecionado ? 'Editar Imposto' : 'Adicionar Imposto'}
            </DialogTitle>
            <DialogDescription>
              {impostoSelecionado 
                ? 'Edite as informações do imposto' 
                : 'Adicione um novo imposto relacionado a esta obra'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="descricao">Descrição *</Label>
              <Input
                id="descricao"
                value={formImposto.descricao}
                onChange={(e) => setFormImposto(prev => ({ ...prev, descricao: e.target.value }))}
                placeholder="Ex: ISS, ICMS, etc."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="valor">Valor (R$) *</Label>
              <Input
                id="valor"
                value={formImposto.valor}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^\d,]/g, '');
                  setFormImposto(prev => ({ ...prev, valor: value }));
                }}
                placeholder="0,00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="data">Data do Pagamento *</Label>
              <Input
                id="data"
                type="date"
                value={formImposto.data}
                onChange={(e) => setFormImposto(prev => ({ ...prev, data: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="observacoes">Observações</Label>
              <Input
                id="observacoes"
                value={formImposto.observacoes}
                onChange={(e) => setFormImposto(prev => ({ ...prev, observacoes: e.target.value }))}
                placeholder="Informações adicionais (opcional)"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImpostoDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={salvarImposto}>
              {impostoSelecionado ? 'Atualizar' : 'Adicionar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


